import { 
  CreatePermissionServiceModel, 
  UpdatePermissionServiceModel, 
  PermissionServiceResponseModel,
  PermissionRolesResponseModel,
  BaseOperationResult,
  PermissionPaginatedResult,
  PaginationParams,
  AuthTokens
} from '../types';
import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';

class PermissionsApiService extends AuthenticatedApiService {
  async getPermissions(params: PaginationParams = {}): Promise<PermissionPaginatedResult> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) {
        queryParams.append('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        queryParams.append('pageSize', params.pageSize.toString());
      }
      if (params.searchTerm) {
        queryParams.append('searchTerm', params.searchTerm);
      }
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      if (params.sortDirection) {
        queryParams.append('sortDirection', params.sortDirection);
      }

      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await this.makeAuthenticatedCall(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get permissions error:', error);
      throw error;
    }
  }

  async getPermissionById(id: string): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSION_BY_ID(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get permission by ID error:', error);
      throw error;
    }
  }

  async createPermission(permission: CreatePermissionServiceModel): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSIONS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permission),
      });

      return await response.json();
    } catch (error) {
      console.error('Create permission error:', error);
      throw error;
    }
  }

  async updatePermission(id: string, permission: UpdatePermissionServiceModel): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSION_BY_ID(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permission),
      });

      return await response.json();
    } catch (error) {
      console.error('Update permission error:', error);
      throw error;
    }
  }

  async deletePermission(id: string): Promise<BaseOperationResult<boolean>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSION_BY_ID(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Delete permission error:', error);
      throw error;
    }
  }

  async getPermissionRoles(id: string): Promise<BaseOperationResult<PermissionRolesResponseModel[]>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PERMISSION_ROLES(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get permission roles error:', error);
      throw error;
    }
  }

  /**
   * Update role assignments for a specific permission
   * Uses /roles/{id}/permissions endpoint as per Swagger spec
   * Iterates through each role and updates its permissions
   * Payload matches Swagger spec: { add: string[], remove: string[] }
   */
  async updatePermissionRoles(permissionId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    try {
      console.log('🔐 Updating role assignments for permission:', permissionId, assignments);
      
      const results: BaseOperationResult<any>[] = [];
      
      // Add permission to roles
      for (const roleId of assignments.add) {
        try {
          const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/permissions`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ add: [permissionId], remove: [] }),
          });

          const result = await response.json();
          results.push(result);
          
          if (!result.succeeded) {
            console.error(`Failed to add permission ${permissionId} to role ${roleId}:`, result.errorDescription);
          }
        } catch (error) {
          console.error(`Error adding permission ${permissionId} to role ${roleId}:`, error);
          results.push({
            succeeded: false,
            errorDescription: `Failed to update role ${roleId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            errorCode: 500,
          });
        }
      }
      
      // Remove permission from roles
      for (const roleId of assignments.remove) {
        try {
          const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/permissions`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ add: [], remove: [permissionId] }),
          });

          const result = await response.json();
          results.push(result);
          
          if (!result.succeeded) {
            console.error(`Failed to remove permission ${permissionId} from role ${roleId}:`, result.errorDescription);
          }
        } catch (error) {
          console.error(`Error removing permission ${permissionId} from role ${roleId}:`, error);
          results.push({
            succeeded: false,
            errorDescription: `Failed to update role ${roleId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            errorCode: 500,
          });
        }
      }
      
      // Return combined result
      const allSucceeded = results.every(r => r.succeeded);
      const failedResults = results.filter(r => !r.succeeded);
      
      if (allSucceeded) {
        return {
          succeeded: true,
          data: results,
        };
      } else {
        return {
          succeeded: false,
          errorDescription: failedResults.map(r => r.errorDescription).join('; '),
          errorCode: failedResults[0]?.errorCode || 500,
        };
      }
    } catch (error) {
      console.error('Update permission roles error:', error);
      throw error;
    }
  }
}

const permissionsApiService = new PermissionsApiService();

export const permissionsService = {
  initialize(tokens: AuthTokens | null, onTokenRefresh: (tokens: AuthTokens) => void) {
    permissionsApiService.setTokens(tokens, onTokenRefresh);
  },
  
  async getPermissions(params: PaginationParams = {}): Promise<PermissionPaginatedResult> {
    return permissionsApiService.getPermissions(params);
  },

  async getPermissionById(id: string): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    return permissionsApiService.getPermissionById(id);
  },

  async createPermission(permission: CreatePermissionServiceModel): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    return permissionsApiService.createPermission(permission);
  },

  async updatePermission(id: string, permission: UpdatePermissionServiceModel): Promise<BaseOperationResult<PermissionServiceResponseModel>> {
    return permissionsApiService.updatePermission(id, permission);
  },

  async deletePermission(id: string): Promise<BaseOperationResult<boolean>> {
    return permissionsApiService.deletePermission(id);
  },

  async getPermissionRoles(id: string): Promise<BaseOperationResult<PermissionRolesResponseModel[]>> {
    return permissionsApiService.getPermissionRoles(id);
  },

  async updatePermissionRoles(permissionId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    return permissionsApiService.updatePermissionRoles(permissionId, assignments);
  },

  isAuthenticated(): boolean {
    return permissionsApiService.isAuthenticated();
  }
};
